/* eslint-disable no-unused-vars */
/**
 * API Client
 *
 * Низкоуровневый HTTP клиент для работы с backend API.
 *
 * АРХИТЕКТУРА:
 * - apiClient (этот файл) — HTTP запросы, interceptors, retry логика
 * - tournamentApi, gameApi — специализированные API сервисы (stateless)
 * - Stores (tournament, auth) — глобальное состояние, бизнес-логика, координация
 *
 * Возможности:
 * - Type-safe запросы с использованием TypeScript generic типов
 * - Автоматическая обработка ошибок
 * - Retry логика для failed requests
 * - Access Token аутентификация (управление токенами)
 * - Request/Response interceptors
 *
 * Управление токенами:
 * - apiClient предоставляет методы для работы с localStorage
 * - authStore координирует логику аутентификации
 * - API сервисы НЕ работают с токенами напрямую
 */

import { API, STORAGE_KEYS, UI } from '@/config/constants';
import type { ApiError } from '@/types/api';

/**
 * Конфигурация для HTTP запроса
 */
interface RequestConfig extends RequestInit {
	/** Добавить access token в заголовки (если есть в localStorage) */
	useAuth?: boolean;
	/** ID турнира для получения access token */
	tournamentId?: number;
	/** Добавить Keycloak access token из useUserStore */
	useKeycloakAuth?: boolean;
	/** Количество попыток при ошибке */
	retries?: number;
	/** Задержка между попытками (мс) */
	retryDelay?: number;
}

/**
 * Ошибка API запроса
 *
 * Расширяет стандартный Error дополнительной информацией из backend.
 * Содержит HTTP статус код, validation errors и traceId для debugging.
 */
export class ApiRequestError extends Error {
	/**
	 * Создаёт новую ошибку API запроса
	 *
	 * @param message - Человекочитаемое сообщение об ошибке
	 * @param statusCode - HTTP статус код ошибки (например, 404, 500)
	 * @param errors - Объект с validation errors из backend (опционально)
	 * @param traceId - Уникальный ID для трассировки запроса на backend (опционально)
	 */
	constructor(
		message: string,
		public statusCode: number,
		public errors?: Record<string, string[]>,
		public traceId?: string,
		public errorCode?: string
	) {
		super(message);
		this.name = 'ApiRequestError';
	}

	/**
	 * Получить первое сообщение об ошибке валидации
	 *
	 * @returns Первое сообщение об ошибке или null, если ошибок валидации нет
	 */
	getFirstValidationError(): string | null {
		if (!this.errors) return null;
		const firstKey = Object.keys(this.errors)[0];
		return firstKey ? this.errors[firstKey][0] : null;
	}

	/**
	 * Проверка на конкретный HTTP статус код
	 *
	 * @param code - HTTP статус код для проверки
	 * @returns true если statusCode совпадает с переданным кодом
	 */
	isStatus(code: number): boolean {
		return this.statusCode === code;
	}
}

/**
 * Тип для request interceptor
 */
type RequestInterceptor = (config: RequestConfig) => RequestConfig | Promise<RequestConfig>;

/**
 * Тип для response interceptor
 * Порядок параметров: сначала data (для трансформации), потом response (для контекста)
 */
type ResponseInterceptor = <T>(data: T, response: Response) => T | Promise<T>;

/**
 * Тип для error interceptor
 */
type ErrorInterceptor = (error: ApiRequestError) => ApiRequestError | Promise<ApiRequestError>;

/**
 * Базовый класс для HTTP запросов
 *
 * Предоставляет type-safe методы для работы с backend API.
 * Включает retry логику, interceptors, обработку ошибок и аутентификацию.
 */
class ApiClient {
	private baseUrl: string;

	private requestInterceptors: RequestInterceptor[] = [];

	private responseInterceptors: ResponseInterceptor[] = [];

	private errorInterceptors: ErrorInterceptor[] = [];

	/**
	 * Создаёт новый instance API клиента
	 *
	 * @param baseUrl - Базовый URL для всех запросов (например, 'http://localhost:5000')
	 */
	constructor(baseUrl: string) {
		this.baseUrl = baseUrl;
	}

	/**
	 * Добавить interceptor для обработки запросов перед отправкой
	 *
	 * @param interceptor - Функция-interceptor для трансформации конфигурации запроса
	 * @public
	 */
	addRequestInterceptor(_interceptor: RequestInterceptor): void {
		this.requestInterceptors.push(_interceptor);
	}

	/**
	 * Добавить interceptor для обработки ответов после получения
	 *
	 * @param interceptor - Функция-interceptor для трансформации данных ответа
	 * @public
	 */
	addResponseInterceptor(_interceptor: ResponseInterceptor): void {
		this.responseInterceptors.push(_interceptor);
	}

	/**
	 * Добавить interceptor для обработки ошибок
	 *
	 * @param interceptor - Функция-interceptor для обработки ошибок
	 * @public
	 */
	addErrorInterceptor(_interceptor: ErrorInterceptor): void {
		this.errorInterceptors.push(_interceptor);
	}

	/**
	 * Получить access token для турнира из localStorage
	 *
	 * @param tournamentId - ID турнира для получения токена
	 * @returns Access token или null, если токен не найден или произошла ошибка
	 * @public
	 */
	getAccessToken(tournamentId: number): string | null {
		if (!tournamentId) return null;

		try {
			const tokensStr = localStorage.getItem(STORAGE_KEYS.tournamentTokens);
			if (!tokensStr) return null;

			const tokens: Record<string, string> = JSON.parse(tokensStr);
			return tokens[tournamentId] || null;
		} catch {
			return null;
		}
	}

	/**
	 * Сохранить access token для турнира в localStorage
	 *
	 * @param tournamentId - ID турнира
	 * @param token - Access token для сохранения
	 * @throws {Error} Если токен не строка или tournamentId не число (неявно через JSON.stringify)
	 * @public
	 */
	saveAccessToken(tournamentId: number, token: string): void {
		const tokensStr = localStorage.getItem(STORAGE_KEYS.tournamentTokens);
		const tokens: Record<string, string> = tokensStr ? JSON.parse(tokensStr) : {};

		tokens[tournamentId] = token;
		localStorage.setItem(STORAGE_KEYS.tournamentTokens, JSON.stringify(tokens));
	}

	/**
	 * Удалить access token для турнира из localStorage
	 *
	 * @param tournamentId - ID турнира для удаления токена
	 * @public
	 */
	removeAccessToken(tournamentId: number): void {
		const tokensStr = localStorage.getItem(STORAGE_KEYS.tournamentTokens);
		if (!tokensStr) return;

		const tokens: Record<string, string> = JSON.parse(tokensStr);
		delete tokens[tournamentId];
		localStorage.setItem(STORAGE_KEYS.tournamentTokens, JSON.stringify(tokens));
	}

	private sleep(ms: number): Promise<void> {
		return new Promise(resolve => setTimeout(resolve, ms));
	}

	// AbortSignal.timeout() is Safari 16+ only; fall back to AbortController for older versions
	private createTimeoutSignal(ms: number): AbortSignal {
		if (typeof (AbortSignal as { timeout?: unknown }).timeout === 'function') {
			return AbortSignal.timeout(ms);
		}
		const controller = new AbortController();
		setTimeout(() => controller.abort(), ms);
		return controller.signal;
	}

	/**
	 * Применить все зарегистрированные request interceptors
	 *
	 * @param config - Конфигурация запроса для обработки
	 * @returns Обработанная конфигурация запроса
	 * @private
	 */
	private async applyRequestInterceptors(config: RequestConfig): Promise<RequestConfig> {
		let result = config;

		for (const interceptor of this.requestInterceptors) {
			result = await interceptor(result);
		}

		return result;
	}

	/**
	 * Применить все зарегистрированные response interceptors
	 *
	 * @template T - Тип данных ответа
	 * @param data - Распарсенные данные ответа
	 * @param response - Объект Response из fetch API
	 * @returns Обработанные данные ответа
	 * @private
	 */
	private async applyResponseInterceptors<T>(data: T, response: Response): Promise<T> {
		let result = data;

		for (const interceptor of this.responseInterceptors) {
			result = await interceptor(result, response);
		}

		return result;
	}

	/**
	 * Применить все зарегистрированные error interceptors
	 *
	 * @param error - Ошибка для обработки
	 * @returns Обработанная ошибка
	 * @private
	 */
	private async applyErrorInterceptors(error: ApiRequestError): Promise<ApiRequestError> {
		let result = error;

		for (const interceptor of this.errorInterceptors) {
			result = await interceptor(result);
		}

		return result;
	}

	/**
	 * Обрабатывает HTTP ошибки и преобразует в ApiRequestError
	 *
	 * @param response - HTTP Response с ошибкой (не ok)
	 * @throws {ApiRequestError} Всегда бросает ошибку после обработки
	 * @private
	 */
	private async handleErrorResponse(response: Response): Promise<never> {
		let errorData: ApiError;

		try {
			errorData = await response.json();
		} catch {
			const statusFallbacks: Record<number, string> = {
				401: 'Unauthorized',
				403: 'Access denied',
				404: 'Not found',
				500: 'Server error'
			};
			const error = new ApiRequestError(
				response.statusText || statusFallbacks[response.status] || 'Unknown error',
				response.status
			);
			throw await this.applyErrorInterceptors(error);
		}

		const error = new ApiRequestError(
			errorData.error || errorData.message || 'Request failed',
			response.status,
			errorData.errors,
			errorData.traceId,
			errorData.errorCode
		);

		throw await this.applyErrorInterceptors(error);
	}

	/**
	 * Выполняет HTTP запрос с обработкой ошибок и retry логикой
	 *
	 * @template T - Тип ожидаемого ответа
	 * @param endpoint - URL endpoint относительно baseUrl (например, '/api/tournament')
	 * @param config - Дополнительная конфигурация запроса
	 * @returns Promise с данными типа T
	 * @throws {ApiRequestError} При ошибке запроса
	 * @throws {Error} При других ошибках (network, timeout)
	 * @private
	 */
	private async request<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
		const {
			useAuth = false,
			tournamentId,
			useKeycloakAuth = false,
			retries = UI.maxRetries,
			retryDelay = UI.retryDelay,
			...fetchConfig
		} = config;

		// Применяем request interceptors
		const processedConfig = await this.applyRequestInterceptors({
			useAuth,
			tournamentId,
			useKeycloakAuth,
			retries,
			retryDelay,
			...fetchConfig
		});

		const url = `${this.baseUrl}${endpoint}`;
		const headers = new Headers(processedConfig.headers);

		// Устанавливаем Content-Type по умолчанию для JSON
		if (!headers.has('Content-Type') && processedConfig.body) {
			headers.set('Content-Type', 'application/json');
		}

		// Добавляем Keycloak access token если требуется (приоритет над tournament token)
		if (useKeycloakAuth) {
			const { useUserStore } = await import('@/stores/user');
			const userStore = useUserStore();
			if (userStore.accessToken) {
				headers.set('Authorization', `Bearer ${userStore.accessToken}`);
			}
		} else if (useAuth && tournamentId) {
			// Добавляем tournament access token если требуется
			const token = this.getAccessToken(tournamentId);
			if (token) {
				headers.set('Authorization', `Bearer ${token}`);
			}
		}

		const requestConfig: RequestInit = {
			...processedConfig,
			headers,
			signal: this.createTimeoutSignal(UI.requestTimeout)
		};

		// Retry логика с exponential backoff
		let lastError: Error | null = null;
		const maxRetries = processedConfig.retries ?? retries;
		const delay = processedConfig.retryDelay ?? retryDelay;

		for (let attempt = 0; attempt <= maxRetries; attempt++) {
			try {
				const response = await fetch(url, requestConfig);

				// Если ответ успешный, парсим и возвращаем
				if (response.ok) {
					let data: T;

					// Для 204 No Content возвращаем null, иначе пытаемся распарсить JSON
					if (response.status === 204) {
						data = null as T;
					} else {
						try {
							const text = await response.text();
							data = text ? JSON.parse(text) : (null as T);
						} catch {
							data = null as T;
						}
					} // Применяем response interceptors
					return await this.applyResponseInterceptors(data, response);
				}

				// Обработка HTTP ошибок
				await this.handleErrorResponse(response);
			} catch (error) {
				lastError = error as Error;

				// Не retry для client-side ошибок (4xx)
				if (error instanceof ApiRequestError && error.statusCode >= 400 && error.statusCode < 500) {
					throw error;
				}

				// Если это последняя попытка, бросаем ошибку
				if (attempt === maxRetries) {
					throw error;
				}

				// Ждём перед следующей попыткой (exponential backoff)
				await this.sleep(delay * (attempt + 1));
			}
		}

		throw lastError;
	}

	/**
	 * Выполняет запрос с телом (POST/PUT/PATCH)
	 *
	 * @template T - Тип ожидаемого ответа
	 * @template D - Тип данных тела запроса
	 * @param method - HTTP метод
	 * @param endpoint - URL endpoint относительно baseUrl
	 * @param data - Данные для отправки в теле запроса (будут сериализованы в JSON)
	 * @param config - Дополнительная конфигурация запроса
	 * @returns Promise с данными типа T
	 * @throws {ApiRequestError} При ошибке запроса
	 * @private
	 */
	private async requestWithBody<T, D = unknown>(
		method: 'POST' | 'PUT' | 'PATCH',
		endpoint: string,
		data?: D,
		config?: RequestConfig
	): Promise<T> {
		return this.request<T>(endpoint, {
			...config,
			method,
			body: data ? JSON.stringify(data) : undefined
		});
	}

	/**
	 * Выполняет HTTP GET запрос
	 *
	 * @template T - Тип ожидаемого ответа
	 * @param endpoint - URL endpoint относительно baseUrl (например, '/api/tournament')
	 * @param config - Дополнительная конфигурация запроса
	 * @returns Promise с данными типа T
	 * @throws {ApiRequestError} При ошибке запроса
	 * @public
	 */
	async get<T>(endpoint: string, config?: RequestConfig): Promise<T> {
		return this.request<T>(endpoint, {
			...config,
			method: 'GET'
		});
	}

	/**
	 * Выполняет HTTP POST запрос
	 *
	 * @template T - Тип ожидаемого ответа
	 * @template D - Тип данных тела запроса
	 * @param endpoint - URL endpoint относительно baseUrl
	 * @param data - Данные для отправки (будут сериализованы в JSON)
	 * @param config - Дополнительная конфигурация запроса
	 * @returns Promise с данными типа T
	 * @throws {ApiRequestError} При ошибке запроса
	 * @public
	 */
	async post<T, D = unknown>(endpoint: string, data?: D, config?: RequestConfig): Promise<T> {
		return this.requestWithBody<T, D>('POST', endpoint, data, config);
	}

	/**
	 * Выполняет HTTP PUT запрос
	 *
	 * @template T - Тип ожидаемого ответа
	 * @template D - Тип данных тела запроса
	 * @param endpoint - URL endpoint относительно baseUrl
	 * @param data - Данные для отправки (будут сериализованы в JSON)
	 * @param config - Дополнительная конфигурация запроса
	 * @returns Promise с данными типа T
	 * @throws {ApiRequestError} При ошибке запроса
	 * @public
	 */
	async put<T, D = unknown>(endpoint: string, data?: D, config?: RequestConfig): Promise<T> {
		return this.requestWithBody<T, D>('PUT', endpoint, data, config);
	}

	/**
	 * Выполняет HTTP PATCH запрос
	 *
	 * @template T - Тип ожидаемого ответа
	 * @template D - Тип данных тела запроса
	 * @param endpoint - URL endpoint относительно baseUrl
	 * @param data - Данные для частичного обновления (будут сериализованы в JSON)
	 * @param config - Дополнительная конфигурация запроса
	 * @returns Promise с данными типа T
	 * @throws {ApiRequestError} При ошибке запроса
	 * @public
	 */
	async patch<T, D = unknown>(endpoint: string, data?: D, config?: RequestConfig): Promise<T> {
		return this.requestWithBody<T, D>('PATCH', endpoint, data, config);
	}

	/**
	 * Выполняет HTTP DELETE запрос
	 *
	 * @template T - Тип ожидаемого ответа
	 * @param endpoint - URL endpoint относительно baseUrl
	 * @param config - Дополнительная конфигурация запроса
	 * @returns Promise с данными типа T
	 * @throws {ApiRequestError} При ошибке запроса
	 * @public
	 */
	async delete<T>(endpoint: string, config?: RequestConfig): Promise<T> {
		return this.request<T>(endpoint, {
			...config,
			method: 'DELETE'
		});
	}
}

/**
 * Default instance API клиента для использования в приложении
 *
 * Это не синглтон - можно создавать дополнительные instances через new ApiClient(),
 * но в большинстве случаев следует использовать этот общий instance.
 */
export const apiClient = new ApiClient(API.baseUrl);

/**
 * Экспортируем класс ApiClient для создания custom instances
 *
 * Используйте это только если нужен клиент с другим baseUrl или для тестирования.
 */
export { ApiClient };
